var Type = require("./Type"),
	Customizing = require("../Customizing");

/**
 * The Numeric class represents a numeric data type. Numeric data can
 * handle several options like thousands delimiter, decimal (scale)
 * and different types like "percent" and "exponential".
 *
 * @param {Object} [oConfig] Configuration object for this numeric representation
 * @param {boolean} [oConfig.autoScale] Applies dynamic scale depending on the specific unit
 * @param {boolean} [oConfig.delimiter] Indicates if a thousands delimiter should be used
 * @param {boolean} [oConfig.displayUnit] Indicating whether the unit of measurement will be shown (default: true)
 * @param {number} [oConfig.scale] A fixed scale that will be applied to all cells within this column,
 * regardless of its particular unit of measurement
 * @param {string} [oConfig.type] Specific type that is handled by this Numeric class
 * @param {string} [oConfig.unit] Specifies a particular unit of measurement
 * @param {string} [oConfig.unitProperty] Specifies a property that contains the unit of measure
 * @constructor
 */
function Numeric(oConfig) {
	Type.call(this, oConfig);

	this.autoScale = oConfig ? oConfig.autoScale : false;
	this.delimiter = oConfig ? oConfig.delimiter : false;
	this.displayUnit = oConfig && typeof oConfig.displayUnit === "boolean" ? oConfig.displayUnit : true;
	this.unit = oConfig ? oConfig.unit : null;
	this.unitProperty = oConfig ? oConfig.unitProperty : null;
	this.scale = oConfig ? oConfig.scale : null;
	this.type = oConfig && typeof oConfig.type === "string" ? oConfig.type.toLowerCase() : null;

	this.styleIds = {};
}

Numeric.prototype = Object.create(Type.prototype);
Numeric.prototype.constructor = Numeric;

Numeric.DEFAULT = {
	numberFormat: 0
};

Numeric.builtin = {
	1: "0",
	2: "0.00",
	3: "#,##0",
	4: "#,##0.00",
	9: "0%",
	10: "0.00%",
	11: "0.00E+00",
	12: "# ?/?",
	13: "# ??/??",
	37: "#,##0;(#,##0)",
	38: "#,##0;[Red](#,##0)",
	39: "#,##0.00;(#,##0.00)",
	40: "#,##0.00;[Red](#,##0.00)",
	48: "##0.0E+0"
};

Numeric.scientificRegEx = /^-?\d?(\.\d)?\d*[eE][+-]?\d{1,3}$/;


/**
 * Returns the unit specific scale.
 *
 * @param {string} sUnit Any unit of measure that has been configured within the customizing
 * @returns {number|null|undefined} A positive integer that represents the decimal scale or null/undefined in
 * case neither dynamic nor fixed scale is available
 * @public
 */
Numeric.prototype.getScale = function(sUnit) {
	var iScale;

	if (this.autoScale) {
		iScale = Customizing.getScaleForUnit(sUnit);
	}

	if (isNaN(iScale)) {
		iScale = this.scale;
	}

	return iScale;
};

/**
 * Returns the numberFormat id of the built-in format or a numberFormat object with the formatting template
 *
 * @param {string} sUnit Unit of measure that is used to resolve the unit specific scale
 * @returns {string} Template for the NumberFormat
 */
Numeric.prototype.getNumberFormatTemplate = function(sUnit) {
	const scale = this.getScale(sUnit);

	// Use built-in template as base
	let template = Numeric.builtin[1];

	/* Add decimal */
	if (typeof scale === "number" && scale > 0) {
		template += ".";

		for (let i = 0; i < scale; i++) {
			template += "0";
		}
	}

	if (this.delimiter) {
		template = "#,##" + template;
	}

	if (this.type === "percentage") {
		template += '%';
	} else if (sUnit && this.displayUnit) {
		template += '\\ "' + sUnit + '"';
	}

	return template;
};

/**
 * Creates the format according to the provided configuration. If the
 * format matches any builtin format, it will return a reference to
 * its ID instead of the format.
 *
 * Both cases can serve the Style class that consumes this information.
 *
 * @param {Object} oData Business object that contains additional data that is used for the formatting
 * @returns {Object} Configuration object for NumberFormat initialization
 * @protected
 */
Numeric.prototype._createConfiguration = function(oData) {
	const config = Object.getPrototypeOf(Numeric.prototype)._createConfiguration.call(this, oData);

	/*
	 * Evaluate unit of measurement first, because a plain built-in
	 * format is used as template but we need to be able to return
	 * to the DEFAULT value in case the unit is empty or invalid.
	 */
	const unit = this.getUnit(oData);
	const scale = this.getScale(unit);

	if (!this.delimiter && typeof scale !== "number" && !unit) {
		config.numberFormat = Numeric.DEFAULT.numberFormat;

		return config;
	}

	// Use built-in template as base
	const template = this.getNumberFormatTemplate(unit);

	config.numberFormat = {
		format: template
	};

	/* Find possible builtin match */
	for (const key in Numeric.builtin) {
		if (Numeric.builtin[key] === template) {
			config.numberFormat = parseInt(key, 10);
		}
	}

	return config;
};

/**
 * Returns the assigned ID of the Type specific Style that is
 * registered in the Styles class.
 *
 * @param {Object} oData Business Object that contains the property for the UoM
 * @param {string|number} [value] Explicit cell value that might be used for semantic formatting
 * @returns {number} ID of the registered Style
 */
Numeric.prototype.getStyleId = function(oData, value) {
	var styleId, unit;

	/* Handle simple numeric without unitProperty */
	if (!this.unitProperty) {
		return Object.getPrototypeOf(Numeric.prototype).getStyleId.call(this, oData, value);
	}

	if (!oData) {
		return 0;
	}

	unit = oData[this.unitProperty];
	styleId = this.styleIds[unit];

	if (typeof styleId === "undefined") {
		this.styleIds[unit] = this.styles.addStyle(this._createConfiguration(oData));
	}

	return this.styleIds[unit];
};

/**
 * Returns the data value of the business object that is bound by
 * this type. The value is normalized so that different input
 * representations will result in the same format.
 *
 * @param {string} sProperty Name of the property that shall be accessed
 * @param {Object} oData Business object that contains the data
 *
 * @returns {string|number|boolean|null} The value of the given property after type specific processing.
 */
Numeric.prototype.getValue = function(sProperty, oData) {
	var aSegments, iScale, iExponent, sValue = oData[sProperty];

	if (typeof sValue == "string" && Numeric.scientificRegEx.test(sValue)) {

		aSegments = sValue.split(/\.|[eE]/g);
		iScale = aSegments.length === 3 ? aSegments[1].length : 0;
		iExponent = Number(aSegments[aSegments.length - 1]);

		/* According to OData V4 Edm.Double is an IEEE 754 binary64 floating-point number (15-17 decimal digits) */
		sValue = Number(sValue).toFixed(Math.max(iScale - iExponent, 0));

		/* Write back the transformed value to avoid subsequent transformations */
		oData[sProperty] = sValue;
	}

	return sValue;
};

/**
 * Checks if the given value can be displayed in the IEEE 754
 * specification format. The function ignores leading and trailing
 * zeros and compares if the length of the actual value does not exceed
 * 15 digits plus an optional decimal separator.
 *
 * The number might exceed 15 digits if it contains leading or
 * trailing zeros.
 *
 * @param {string} sValue Numeric value that needs to be checked
 * @param {Object} oData Business object that contains additional information like unit and scale
 * @returns {boolean} Whether the number needs to be formatted as BigNumber or not
 */
Numeric.prototype.isIEEE754Compliant = function(sValue, oData) {
	var bCompliant = false;
	var sUnit = this.getUnit(oData);
	var iScale = this.getScale(sUnit);

	if (typeof sValue == "number") {
		// eslint-disable-next-line no-param-reassign
		sValue = "" + sValue;
	}

	if (typeof sValue == "string") {
		bCompliant = sValue
			.trim()
			.replace(/^-*0*|0+$/g, "")
			.split(".")
			.reduce(function(acc, value, index) {

				/*
				 * The array cannot have more than 2 entries, so we take the scale
				 * into account for all indices higher than 0.
				 */
				var length = index > 0 && typeof iScale === "number" ? Math.min(iScale, value.length) : value.length;

				/*
				* Returns 1 if the value left of the separator is
				* zero and was cut of by removing leading and trailing zeros
				*/
				return acc + length || 1;
			}, 0) <= 15;
	}

	return bCompliant;
};

/**
 * Resolves the unit of measurement that is required for the specific formatting.
 * @param {Object} oData Data object that represents the row.
 * @returns {string} Unit of measurement or empty string.
 * @protected
 */
Numeric.prototype.getUnit = function(oData) {
	var unit;

	/* Obtain unit of measurement if available */
	if (this.type === "percentage") {
		unit = "%";
	} else if (typeof this.unitProperty === "string" && oData) {
		unit = oData[this.unitProperty];
	} else if (typeof this.unit === "string") {
		unit = this.unit;
	}

	/* Ignore invalid units */
	if (typeof unit !== "string" || unit === '"') {
		unit = "";
	}

	return unit || "";
};

module.exports = Numeric;